import { SegmentedControl } from "@mantine/core";
import { IconCircleCheck } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { numericFormatter } from "react-number-format";
import type { DataGaps } from "../../../../../../../../utils/backend-types";
import { THEME_VARS } from "../../../../../../../../utils/constants";
import { DATE_DISPLAY_FORMAT } from "../../../../../../../../utils/dates/dates.constants";
import type { Frequency } from "../../../../../../../../utils/enums";
import {
  getMilliSecsFromFrequency,
  getMinsFromFrequency
} from "../../../../../../../../utils/frequencyHelpers";
import { getDefaultNumericFormatterProps } from "../../../../../../../../utils/getDefaultNumericFormatterProps";
import { NoDataMessage } from "../../../../../../../BuildingBlocks/NoDataMessage/NoDataMessage";
import { MantineBarChart } from "../../../../../../../Charts/BarChart/MantineBarChart";
import { mapMeasurandToLabel } from "../utils/mapMeasurandToLabel";
import "./DataGapsChart.scss";

type Resolution = "day" | "week" | "month";

type SlotData = {
  slotName: string;
  slotStart: DateTime;
  slotEnd: DateTime;
  slotWidth: number;
  gaps: Array<number>;
};

export interface DataGapsChartProps {
  dataGaps: DataGaps;
  frequency: Frequency;
  selectedTimeRange: [DateTime, DateTime];
}

function DataGapsChart({
  dataGaps,
  frequency,
  selectedTimeRange
}: DataGapsChartProps) {
  const seriesNames = Object.keys(dataGaps).filter((key) => key.includes("."));
  const monthsToShow = Math.round(
    selectedTimeRange[1].diff(selectedTimeRange[0], "months").toObject()
      .months || 0
  );
  const defaultResolution =
    monthsToShow >= 12 ? "month" : monthsToShow > 1 ? "week" : "day";

  const [selectedSeriesKey, setSelectedSeriesKey] = useState<
    string | undefined
  >(seriesNames[0]);
  const [currentChartResolution, setCurrentChartResolution] =
    useState<Resolution>(
      monthsToShow >= 12 ? "month" : monthsToShow > 1 ? "week" : "day"
    );
  const [zoomTimeRange, setZoomTimeRange] = useState<[DateTime, DateTime]>([
    selectedTimeRange[0],
    selectedTimeRange[1]
  ]);

  const slotsData: Array<SlotData> = useMemo(() => {
    const frequencyInMinutes = getMinsFromFrequency(frequency);
    const frequencyInMillies = getMilliSecsFromFrequency(frequency);
    const tempSlotsData: Array<SlotData> = [];

    for (
      let slotIterator = zoomTimeRange[0];
      slotIterator < zoomTimeRange[1];
      slotIterator = slotIterator
        .startOf(currentChartResolution)
        .plus({ [currentChartResolution]: 1 })
    ) {
      const firstInSlot = slotIterator;
      let lastInSlot = slotIterator
        .startOf(currentChartResolution)
        .plus({ [currentChartResolution]: 1 })
        .minus({
          minutes: frequencyInMinutes
        });
      if (lastInSlot > zoomTimeRange[1]) {
        lastInSlot = zoomTimeRange[1];
      }

      // The not-rounded width can be a too big if the slot is not a full frequency interval
      const widthOfSlot = Math.floor(
        (lastInSlot.diff(firstInSlot, "minutes").minutes + frequencyInMinutes) /
          frequencyInMinutes
      );

      const gapsInSlot: Array<number> = seriesNames.map((seriesName) =>
        dataGaps[seriesName].gaps.reduce((counter, gap) => {
          const firstInGap = DateTime.fromISO(gap[0]);
          const lastInGap = DateTime.fromISO(gap[1]);

          const hasIntersection =
            firstInGap <= lastInSlot && lastInGap >= firstInSlot;
          if (!hasIntersection) {
            return counter;
          }

          const intersectionStartInMillis = Math.max(
            firstInGap.toMillis(),
            firstInSlot.toMillis()
          );
          const intersectionEndInMillis = Math.min(
            lastInGap.toMillis(),
            lastInSlot.toMillis()
          );
          const amountOfGaps =
            (intersectionEndInMillis +
              frequencyInMillies -
              intersectionStartInMillis) /
            frequencyInMillies;

          return counter + amountOfGaps;
        }, 0)
      );

      tempSlotsData.push({
        slotName:
          currentChartResolution === "day"
            ? slotIterator.toFormat(DATE_DISPLAY_FORMAT)
            : currentChartResolution === "week"
              ? "KW " + slotIterator.plus({ days: 0 }).toFormat("WW") // plus 0 required due to https://github.com/moment/luxon/issues/740
              : slotIterator.toFormat("MM/yyyy"),
        slotStart: firstInSlot,
        slotEnd: lastInSlot,
        slotWidth: widthOfSlot,
        gaps: gapsInSlot
      });
    }

    return tempSlotsData;
  }, [currentChartResolution, frequency, zoomTimeRange, dataGaps, seriesNames]);

  const chartData = useMemo(
    () =>
      slotsData.map((slot) => ({
        x: slot.slotName,
        x_total: slot.slotWidth,
        xStart: slot.slotStart,
        xEnd: slot.slotEnd,
        ...seriesNames.reduce(
          (result, seriesName, index) => ({
            ...result,
            [seriesName]: (slot.slotWidth - slot.gaps[index]) / slot.slotWidth
          }),
          {}
        ),
        ...seriesNames.reduce(
          (result, seriesName, index) => ({
            ...result,
            [`${seriesName}_total`]: slot.gaps[index]
          }),
          {}
        )
      })),
    [seriesNames, slotsData]
  );

  function handleZoom(slotStart: DateTime, slotEnd: DateTime) {
    setZoomTimeRange([slotStart, slotEnd]);
    setCurrentChartResolution(
      currentChartResolution === "month" ? "week" : "day"
    );
  }

  function handleZoomReset() {
    setZoomTimeRange(selectedTimeRange);
    setCurrentChartResolution(defaultResolution);
  }

  return (
    <div className="DataGapsChart" data-testid="data-gaps-chart-wrapper">
      {Object.keys(dataGaps).filter((key) => key.includes(".")).length ===
        0 && (
        <div className="no-data-display">
          <IconCircleCheck color={THEME_VARS.success[6]} size="3rem" />
          <NoDataMessage
            fw={600}
            fz="2rem"
            message="Keine Datenlücken!"
            ms="sm"
            p={0}
          />
        </div>
      )}
      {seriesNames.length > 1 && (
        <SegmentedControl
          data={seriesNames.map((seriesKey) => ({
            value: seriesKey,
            label: mapMeasurandToLabel(seriesKey.split(".").at(-1) || "")
          }))}
          mb="lg"
          value={selectedSeriesKey}
          onChange={(seriesKey) => setSelectedSeriesKey(seriesKey)}
        />
      )}
      <MantineBarChart
        data={chartData}
        data-testid="data-gaps-chart"
        dataKey="x"
        getBarColor={(value) =>
          value < 1
            ? value === 0
              ? "var(--mantine-color-red-9)"
              : "var(--mantine-color-yellow-5)"
            : "var(--mantine-color-lime-6)"
        }
        h={450}
        minBarSize={1}
        series={
          selectedSeriesKey
            ? [
                {
                  label: mapMeasurandToLabel(
                    selectedSeriesKey.split(".").at(-1) || ""
                  ),
                  name: selectedSeriesKey
                }
              ]
            : []
        }
        tooltipProps={{
          formatter: (_value, name, item) =>
            item.payload.x_total -
            item.payload[`${name}_total`] +
            "/" +
            item.payload.x_total +
            " Zeitstempel",
          labelFormatter: (label, payload) => {
            if (payload.length === 0) {
              return label;
            }

            const startString = DateTime.fromISO(
              payload[0].payload.xStart
            ).toFormat(DATE_DISPLAY_FORMAT);
            const endString = DateTime.fromISO(
              payload[0].payload.xEnd
            ).toFormat(DATE_DISPLAY_FORMAT);
            if (startString === endString) {
              return startString;
            }
            return startString + " - " + endString;
          }
        }}
        withLegend={false}
        xAxisLabel="Zeitraum"
        xAxisProps={{ angle: 90, height: 75, textAnchor: "start" }}
        yAxisLabel="Vollständigkeit der Daten"
        yAxisProps={{
          domain: [0, 1],
          tickFormatter: (value) =>
            numericFormatter((value * 100).toString(), {
              ...getDefaultNumericFormatterProps(),
              suffix: " %"
            })
        }}
        zoomable={true}
        onZoom={currentChartResolution !== "day" ? handleZoom : undefined}
        onZoomReset={
          currentChartResolution !== defaultResolution
            ? handleZoomReset
            : undefined
        }
      />
    </div>
  );
}

export { DataGapsChart };
